import React, { useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { format, addMilliseconds } from "date-fns";
import { getTimezoneOffset } from "date-fns-tz";
import { setTimeZones } from "@/redux/actions/parameters";
import { getUTCDate } from "@/utils/miscellaneous";
import SelectInput from "@/components/inputs/SelectInput";

const DEFAULT_TIME_ZONE_VALUE = process.env.NEXT_PUBLIC_DEFAULT_TIME_ZONE_ID;

const ITEM_TEMPLATE =
  "<div class='flex justify-between w-full'>" +
  "<span class='font-semibold'>${text}</span>" +
  "<span class='text-gray-500 dark:text-gray-400'>${time}</span>" +
  "</div>";

/**
 * TimeZoneSelect — Syncfusion DropDownList pre-wired to the timeZones Redux store.
 *
 * Drop-in replacement for the legacy react-select `TimeZoneSelectBox`.
 * Emits the selected `parameterId` (string) via `onChange`.
 * Preserves the live-clock behaviour: when the dropdown is open a 1 s
 * interval checks whether the minute has ticked over and refreshes the
 * displayed times in the store.
 *
 * @example
 * <Controller
 *   name="timeZoneId"
 *   control={control}
 *   render={({ field }) => (
 *     <TimeZoneSelect
 *       placeholder="Time Zone"
 *       value={field.value}
 *       onChange={field.onChange}
 *       error={errors.timeZoneId?.message}
 *     />
 *   )}
 * />
 */
export default function TimeZoneSelect({
  placeholder = "Time Zone",
  value,
  onChange,
  error,
  disabled = false,
  className,
  ...props
}) {
  const dispatch = useDispatch();
  const interval = useRef(null);
  const minutes = useRef(new Date().getMinutes());
  const { timeZones: records } = useSelector(state => state.timeZones);

  const dataSource = useMemo(() => {
    if (!records?.length) return [];
    return records.map(tz => ({
      text: tz.name,
      value: tz.parameterId,
      time: tz.time || format(addMilliseconds(getUTCDate(), getTimezoneOffset(tz.value)), "p"),
    }));
  }, [records]);

  const handleOpen = useCallback(() => {
    if (!records?.length) return;
    interval.current = setInterval(() => {
      if (new Date().getMinutes() !== minutes.current) {
        const updated = records.map(tz => ({
          ...tz,
          time: format(addMilliseconds(getUTCDate(), getTimezoneOffset(tz.value)), "p"),
        }));
        dispatch(setTimeZones(updated));
        minutes.current = new Date().getMinutes();
      }
    }, 1000);
  }, [dispatch, records]);

  const handleClose = useCallback(() => {
    clearInterval(interval.current);
  }, []);

  return (
    <SelectInput
      placeholder={placeholder}
      dataSource={dataSource}
      value={value ?? DEFAULT_TIME_ZONE_VALUE}
      onChange={onChange}
      error={error}
      disabled={disabled}
      className={className}
      itemTemplate={ITEM_TEMPLATE}
      open={handleOpen}
      close={handleClose}
      {...props}
    />
  );
}

TimeZoneSelect.propTypes = {
  /** Floating-label / placeholder text */
  placeholder: PropTypes.string,
  /** Controlled value — timeZone parameterId */
  value: PropTypes.any,
  /** Change handler — receives the selected parameterId */
  onChange: PropTypes.func,
  /** Validation error message */
  error: PropTypes.string,
  /** Fully disabled */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
};

